import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { addParticipant, MAX_PARTICIPANTS } from '@/data/participants';
import type { Discipline } from '@/data/types';

interface Props {
  tournamentId: string;
  discipline: Discipline;
  currentCount: number;
}

export function AddParticipantForm({ tournamentId, discipline, currentCount }: Props) {
  const [name1, setName1] = useState('');
  const [club1, setClub1] = useState('');
  const [name2, setName2] = useState('');
  const [club2, setClub2] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const atLimit = currentCount >= MAX_PARTICIPANTS;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (atLimit) {
      toast.error(`Максимум ${MAX_PARTICIPANTS} участников`);
      return;
    }
    const trimmed1 = name1.trim();
    const trimmed2 = name2.trim();
    if (!trimmed1) {
      toast.error('Введите имя');
      return;
    }
    if (discipline === 'DOUBLES' && !trimmed2) {
      toast.error('Для пары нужно два имени');
      return;
    }

    setSubmitting(true);
    try {
      const players = discipline === 'SINGLES'
        ? [{ name: trimmed1, ...(club1.trim() ? { club: club1.trim() } : {}) }]
        : [
            { name: trimmed1, ...(club1.trim() ? { club: club1.trim() } : {}) },
            { name: trimmed2, ...(club2.trim() ? { club: club2.trim() } : {}) },
          ];
      await addParticipant(tournamentId, { players });
      setName1('');
      setClub1('');
      setName2('');
      setClub2('');
      toast.success('Добавлен');
    } catch (err) {
      toast.error('Не получилось', { description: String((err as Error).message) });
    } finally {
      setSubmitting(false);
    }
  }

  const optional = (
    <span className="t-soft" style={{ fontWeight: 400 }}>
      {' '}(необязательно)
    </span>
  );

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gap: 4 }}>
        <label className="label">
          {discipline === 'SINGLES' ? 'Имя' : 'Имя первого игрока'}
        </label>
        <input
          className="input"
          value={name1}
          onChange={(e) => setName1(e.target.value)}
          placeholder="Иванов Иван"
        />
      </div>

      <div style={{ display: 'grid', gap: 4 }}>
        <label className="label">Клуб{optional}</label>
        <input
          className="input"
          value={club1}
          onChange={(e) => setClub1(e.target.value)}
        />
      </div>

      {discipline === 'DOUBLES' && (
        <>
          <div style={{ display: 'grid', gap: 4 }}>
            <label className="label">Имя второго игрока</label>
            <input
              className="input"
              value={name2}
              onChange={(e) => setName2(e.target.value)}
              placeholder="Петров Пётр"
            />
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            <label className="label">Клуб второго{optional}</label>
            <input
              className="input"
              value={club2}
              onChange={(e) => setClub2(e.target.value)}
            />
          </div>
        </>
      )}

      <button
        type="submit"
        className="btn btn-primary"
        disabled={submitting || atLimit}
      >
        {submitting ? <span className="spinner" /> : <Plus size={14} />}
        {submitting ? 'Добавляем...' : 'Добавить участника'}
      </button>

      {atLimit && (
        <p className="help" style={{ color: 'var(--red)' }}>
          Достигнут лимит {MAX_PARTICIPANTS}. Удалите кого-то, чтобы добавить нового.
        </p>
      )}
    </form>
  );
}
